import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import useAuth from '../../Context/Hooks/useCustomAuth';
import MicrosoftIcon from '../../Assets/icons/MicrosoftIcon';

// ─── Page ─────────────────────────────────────────────────────────────────────

const LoginPage = () => {
  const { instance } = useMsal();
  const { loginWithEmail, loginWithMicrosoft } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingMs, setLoadingMs] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Preencha email e password.'); return; }
    setError('');
    setLoadingEmail(true);
    try {
      await loginWithEmail(email, password);
    } catch (err) {
      setError(err.message || 'Credenciais inválidas.');
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setLoadingMs(true);
    try {
      await loginWithMicrosoft(instance);
    } catch (err) {
      setError(err.message || 'Erro ao autenticar com Microsoft.');
    } finally {
      setLoadingMs(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f4f5f7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          bgcolor: 'background.paper',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(16,24,40,0.10)',
          overflow: 'hidden',
        }}
      >
        {/* ── Header strip ── */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            px: 4,
            py: 3.5,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>
            Gateway
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', mt: 0.25 }}>
            Plataforma de Contagem de Pessoas
          </Typography>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ px: 4, py: 4 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary', mb: 2.5 }}>
            Iniciar sessão
          </Typography>

          {error && (
            <Alert
severity="error"
sx={{ mb: 2.5, fontSize: 13 }}
            >
              {error}
            </Alert>
          )}

          {/* ── Microsoft button ── */}
          <Button
            fullWidth
            variant="outlined"
            onClick={handleMicrosoftLogin}
            disabled={loadingMs || loadingEmail}
            startIcon={loadingMs ? <CircularProgress size={16} /> : <MicrosoftIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              borderColor: 'divider',
              color: 'text.primary',
              py: 1.25,
              mb: 2.5,
              '&:hover': { bgcolor: '#f9fafb', borderColor: '#c0c4cc' },
            }}
          >
            Entrar com conta Microsoft
          </Button>

          {/* ── Divider ── */}
          <Divider sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 12, color: 'text.disabled', px: 1 }}>ou</Typography>
          </Divider>

          {/* ── Email + Password form ── */}
          <Stack
component="form"
onSubmit={handleEmailLogin}
spacing={2}
          >
            <TextField
              label="Email"
              type="email"
              size="small"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={loadingEmail || loadingMs}
              sx={{ '& .MuiInputBase-root': { fontSize: 13.5 } }}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              size="small"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={loadingEmail || loadingMs}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword
                        ? <VisibilityOffIcon sx={{ fontSize: 18 }} />
                        : <VisibilityIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiInputBase-root': { fontSize: 13.5 } }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loadingEmail || loadingMs}
              sx={{ textTransform: 'none', fontWeight: 600, py: 1.1, fontSize: '0.875rem' }}
            >
              {loadingEmail ? <CircularProgress
size={18}
color="inherit"
              /> : 'Entrar'}
            </Button>
          </Stack>

          {/* ── Forgot password ── */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography
              component="span"
              sx={{
                fontSize: 12.5,
                color: 'text.disabled',
                cursor: 'default',
              }}
            >
              Esqueceu a password? Contacte o administrador.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
